<template>
  <div class="glass-panel p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">Cost Anomalies</h2>
      <div class="flex gap-2">
        <button 
          v-for="sev in severityLevels" 
          :key="sev"
          @click="selectedSeverity = sev"
          :class="selectedSeverity === sev ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          class="px-3 py-1 rounded-lg text-sm font-medium transition-colors capitalize"
        >
          {{ sev }}
        </button>
      </div>
    </div>
    
    <div v-if="filteredAnomalies.length === 0" class="text-center py-12">
      <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-gray-500 dark:text-gray-400">No anomalies detected</p>
    </div>
    
    <div v-else class="space-y-4">
      <div 
        v-for="anomaly in filteredAnomalies" 
        :key="anomaly.id"
        :class="[getSeverityBorderClass(anomaly.severity), getSeverityBgClass(anomaly.severity)]"
        class="p-4 rounded-lg border-l-4 transition-all hover:shadow-md"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <span :class="getSeverityBadgeClass(anomaly.severity)" class="px-2 py-1 rounded text-xs font-bold uppercase">
                {{ anomaly.severity }}
              </span>
              <span class="font-semibold text-gray-900 dark:text-white">{{ anomaly.warehouse }}</span>
              <span class="text-sm text-gray-500 dark:text-gray-400">{{ formatRelativeTime(anomaly.timestamp) }}</span>
            </div>
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-3">{{ anomaly.description }}</p>
            <div class="flex gap-6 text-sm">
              <div>
                <span class="text-gray-500 dark:text-gray-400">Expected:</span>
                <span class="ml-2 font-medium text-gray-700 dark:text-gray-300">{{ formatCurrency(anomaly.expectedCost) }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Actual:</span>
                <span class="ml-2 font-bold text-red-600 dark:text-red-400">{{ formatCurrency(anomaly.cost) }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Difference:</span>
                <span class="ml-2 font-bold text-red-600 dark:text-red-400">
                  +{{ formatCurrency(anomaly.cost - anomaly.expectedCost) }}
                </span>
              </div>
            </div>
            <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">
              User: {{ anomaly.user }}
            </div>
          </div>
          <div class="flex gap-2 ml-4">
            <button 
              @click="$emit('investigate', anomaly)"
              class="px-3 py-1 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              Investigate
            </button>
            <button 
              @click="dismissAnomaly(anomaly.id)"
              class="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  anomalies: Array<{
    id: string
    timestamp: string
    type: 'spike' | 'unusual_pattern'
    severity: 'info' | 'warning' | 'critical'
    warehouse: string
    user: string
    cost: number
    expectedCost: number
    description: string
  }>
}>()

const emit = defineEmits(['investigate', 'dismiss'])

const { formatCurrency, formatRelativeTime } = useCostFormatting()

const severityLevels = ['all', 'critical', 'warning', 'info']
const selectedSeverity = ref('all')

const filteredAnomalies = computed(() => {
  if (selectedSeverity.value === 'all') {
    return props.anomalies
  }
  return props.anomalies.filter(a => a.severity === selectedSeverity.value)
})

const getSeverityBorderClass = (severity: string): string => {
  const classes = {
    critical: 'border-red-500',
    warning: 'border-yellow-500',
    info: 'border-blue-500'
  }
  return classes[severity as keyof typeof classes] || classes.info
}

const getSeverityBgClass = (severity: string): string => {
  const classes = {
    critical: 'bg-red-50 dark:bg-red-500/10',
    warning: 'bg-yellow-50 dark:bg-yellow-500/10',
    info: 'bg-blue-50 dark:bg-blue-500/10'
  }
  return classes[severity as keyof typeof classes] || classes.info
}

const getSeverityBadgeClass = (severity: string): string => {
  const classes = {
    critical: 'bg-red-600 text-white',
    warning: 'bg-yellow-600 text-white',
    info: 'bg-blue-600 text-white'
  }
  return classes[severity as keyof typeof classes] || classes.info
}

const dismissAnomaly = (id: string) => {
  emit('dismiss', id)
}
</script>
